import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { WireInPreview, isPfsName } from './WireInPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ROLE_LABELS = { INVESTOR: 'Investor', INVESTOR_FEE: 'Investor (origination entitlement)', PARTNER: 'Partner' };

/**
 * 10/28 (Josh: "another way to add syndicators on the fly on this page, and not only rely on sending out for
 * syndication"): one position straight onto the deal — same POST /syndications the deal page's Manage Syndicators
 * cart uses, same wire-in preview, management fee ASKED every time (standing policy). `row` = a Needs Syndication row.
 */
export default function AddSyndicatorDialog({ row, onClose, onDone }) {
  const { getAuthHeaders } = useAuth();
  const [investors, setInvestors] = useState([]);
  const [q, setQ] = useState('');
  const [form, setForm] = useState({ syndicator_id: '', ownership_percentage: '', role: 'INVESTOR', management_fee_pct: '' });
  const [saving, setSaving] = useState(false);
  const available = Number(row?.available_pct || 0);

  useEffect(() => {
    if (!row) return;
    setForm({ syndicator_id: '', ownership_percentage: '', role: 'INVESTOR', management_fee_pct: '' }); setQ('');
    (async () => {
      try { const r = await axios.get(`${API}/syndication-offers/investors`, { headers: { ...getAuthHeaders(), 'X-Tenant': 'live' } }); setInvestors(Array.isArray(r.data) ? r.data : []); }
      catch { setInvestors([]); }
    })();
  }, [row, getAuthHeaders]);

  const options = useMemo(() => {
    const s = q.trim().toLowerCase();
    return investors.filter((i) => !s || (i.name || '').toLowerCase().includes(s) || (i.email || '').toLowerCase().includes(s)).slice(0, 200);
  }, [investors, q]);
  const picked = investors.find((i) => i.id === form.syndicator_id);
  // 11/04 (Josh): a contact marked "Origination entitlement" comes in as Inv (Fee) by default; still editable.
  const pickInvestor = (id) => { const inv = investors.find((i) => i.id === id); setForm((f) => ({ ...f, syndicator_id: id, role: inv?.collect_fees ? 'INVESTOR_FEE' : 'INVESTOR' })); };
  const pfs = isPfsName(picked?.name);
  const defaultFee = row?.is_iso_originated && !pfs ? 2 : 0;
  const pct = parseFloat(form.ownership_percentage);
  const financials = row ? { funding_amount: row.funding_amount, origination_fee: row.origination_fee, broker_commission: row.broker_commission, is_iso_originated: row.is_iso_originated } : null;

  const submit = async () => {
    if (!form.syndicator_id) { toast.error('Pick a syndicator'); return; }
    if (!(pct > 0)) { toast.error('Enter a percentage'); return; }
    if (pct > available + 0.0001) { toast.error(`Only ${available}% is available on #${row.deal_id_number}`); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/syndications`, {
        deal_id: row.deal_id, syndicator_id: form.syndicator_id, ownership_percentage: pct, role: form.role,
        management_fee_pct: form.management_fee_pct === '' ? null : parseFloat(form.management_fee_pct),
      }, { headers: { ...getAuthHeaders(), 'X-Tenant': 'live' } });
      toast.success(`${picked?.name || 'Syndicator'} added at ${pct}% on #${row.deal_id_number}`);
      onDone && (await onDone());
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not add the position');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="h-4 w-4" /> Add a syndicator — #{row?.deal_id_number} {row?.deal_name}</DialogTitle></DialogHeader>
        {row && (
          <div className="space-y-3 text-sm">
            <p className="text-xs text-slate-500">Creates the position now (no offer email). {available}% available{row.pfs_pct ? ` · PFS holds ${row.pfs_pct}%` : ''}.</p>
            <div>
              <Label className="text-xs">Syndicator</Label>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to search investors…" className="h-9 mb-1" />
              <Select value={form.syndicator_id} onValueChange={pickInvestor}>
                <SelectTrigger className="h-9" data-testid="add-synd-contact"><SelectValue placeholder={investors.length ? 'Pick a contact' : 'Loading investors…'} /></SelectTrigger>
                <SelectContent>
                  {options.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}{i.email ? ` · ${i.email}` : ''}</SelectItem>)}
                  {options.length === 0 && <div className="px-2 py-1 text-xs text-slate-400">No match</div>}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div><Label className="text-xs">Percentage</Label><Input type="number" step="0.01" min="0" max={available} value={form.ownership_percentage} onChange={(e) => setForm({ ...form, ownership_percentage: e.target.value })} className="h-9" placeholder={`≤ ${available}`} data-testid="add-synd-pct" /></div>
              <div><Label className="text-xs">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs" title="Management fee (2% upcharge) — asked every time; charged up front on its own line">Mgmt fee %</Label><Input type="number" step="0.01" min="0" value={form.management_fee_pct} onChange={(e) => setForm({ ...form, management_fee_pct: e.target.value })} className="h-9" placeholder={row.is_iso_originated && !pfs ? '2 (ISO deal)' : '0'} /></div>
            </div>
            {pct > 0 && <WireInPreview role={form.role} ownershipPct={pct} dealFinancials={financials} managementFeePct={form.management_fee_pct === '' ? defaultFee : form.management_fee_pct} />}
            {!row.has_commission && <p className="text-xs text-amber-700">This deal has no broker commission on file; the position is created without a commission share until one is attached.</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !form.syndicator_id || !(pct > 0)}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />} Add position</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
